import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Download, Printer } from 'lucide-react';
import { getSessionDetails } from '../../lib/api';
import { saveElementAsPdf, printElementInWindow } from '../../lib/pdf';
import ChargingInvoiceDocument, { generateInvoiceNumber, normalizeChargingInvoice } from './ChargingInvoiceDocument';

const InvoicePopup = ({ session, onClose }) => {
  const invoiceRef = useRef(null);
  const [invoicePdfLoading, setInvoicePdfLoading] = useState(false);
  const [resolvedInvoice, setResolvedInvoice] = useState(null);

  const invoice = useMemo(() => normalizeChargingInvoice(resolvedInvoice || session), [resolvedInvoice, session]);

  useEffect(() => {
    let cancelled = false;

    const loadInvoiceData = async () => {
      if (!session) {
        setResolvedInvoice(null);
        return;
      }

      const fallbackInvoice = normalizeChargingInvoice({
        ...session,
        client_name: session.client_name || session.user_name || session.full_name || session.client?.full_name || session.user?.full_name,
        client_email: session.client_email || session.user_email || session.email || session.client?.email || session.user?.email,
        client_phone: session.client_phone || session.user_phone || session.phone || session.client?.phone || session.user?.phone,
      });
      setResolvedInvoice(fallbackInvoice);

      // session_id = session UUID; id may be an invoice UUID — prefer session_id for lookup
      const lookupId = session.session_id || session.id;
      if (!lookupId) {
        return;
      }

      try {
        const response = await getSessionDetails(lookupId);
        if (cancelled) {
          return;
        }

        const details = response.data || {};
        const detailsSession = details.session || {};
        setResolvedInvoice(normalizeChargingInvoice({
          ...session,
          ...detailsSession,
          client_name: details.user?.full_name || session.client_name || session.user_name || session.full_name,
          client_email: details.user?.email || session.client_email || session.user_email || session.email,
          client_phone: details.user?.phone || session.client_phone || session.user_phone || session.phone,
          user_phone: details.user?.phone || session.user_phone || session.client_phone || session.phone,
          charger_name: details.charger?.name || details.charger?.charger_id || session.charger_name || session.charger_id,
          charger_id: details.charger?.charger_id || session.charger_id,
          charger_price_per_kwh: details.session?.charger_price_per_kwh || details.charger?.price_per_kwh || session.charger_price_per_kwh,
          site_name: details.site?.name || session.site_name,
          site_address: details.site?.address || session.site_address,
          site_city: details.site?.city || session.site_city,
          payment_method: detailsSession.payment_method || session.payment_method,
        }));
      } catch {
        if (!cancelled) {
          setResolvedInvoice(fallbackInvoice);
        }
      }
    };

    loadInvoiceData();

    return () => {
      cancelled = true;
    };
  }, [session]);

  const handleGenerateInvoicePDF = useCallback(async () => {
    if (!invoiceRef.current || !invoice) return;
    try {
      setInvoicePdfLoading(true);
      await saveElementAsPdf(invoiceRef.current, `Fatura-${generateInvoiceNumber(invoice)}.pdf`);
    } catch (error) {
      console.error('Failed to generate invoice pdf:', error);
    } finally {
      setInvoicePdfLoading(false);
    }
  }, [invoice]);

  const handlePrint = () => {
    if (!invoiceRef.current) return;
    printElementInWindow(invoiceRef.current, {
      title: `Fatura-${generateInvoiceNumber(invoice)}`,
    }).catch((error) => {
      console.error('Failed to print invoice:', error);
    });
  };

  useEffect(() => {
    if (!session?._autoDownload) return;
    const timer = setTimeout(() => {
      handleGenerateInvoicePDF();
    }, 250);
    return () => clearTimeout(timer);
  }, [handleGenerateInvoicePDF, session]);

  if (!session || !invoice) return null;

  return (
    <Dialog open={!!session} onOpenChange={() => onClose()}>
      <DialogContent className="invoice-popup-content max-w-3xl bg-zinc-900 border-white/10 p-0 overflow-hidden">
        <DialogHeader className="invoice-popup-chrome px-6 py-4 border-b border-white/10">
          <DialogTitle className="flex items-center justify-between">
            <span>Fatura #{generateInvoiceNumber(invoice)}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Printo
              </Button>
              <Button
                size="sm"
                onClick={handleGenerateInvoicePDF}
                disabled={invoicePdfLoading}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Download className="w-4 h-4 mr-2" />
                {invoicePdfLoading ? 'Duke gjeneruar...' : 'Shkarko PDF'}
              </Button>
            </div>
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Pamje e plotë e faturës me formatin standard.
          </DialogDescription>
        </DialogHeader>

        <div className="invoice-popup-scroll max-h-[70vh] overflow-y-auto">
          <div ref={invoiceRef} className="invoice-print-area">
            <ChargingInvoiceDocument invoice={invoice} />
          </div>
        </div>

        <DialogFooter className="invoice-popup-chrome gap-2 px-6 py-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Mbyll
          </Button>
          <Button onClick={handleGenerateInvoicePDF} className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={invoicePdfLoading}>
            <Download className="w-4 h-4" />
            Shkarko PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoicePopup;
